import {
  Flex,
  forwardRef,
  Icon,
  Input,
  InputGroup,
  InputProps,
  InputRightElement,
} from '@chakra-ui/react'
import React, { ForwardedRef, useId } from 'react'
import SearchIcon from '~/assets/icons/icon-search.svg?react'
import { MangoCloseButton } from '~/components/shared/MangoCloseButton'
import { useSearchInputString } from '~/pages/WebPhone/atoms/SearchInput.atom'
import { SearchValue } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'
import { tryGetValueOrDefault } from '~/utils/common'

import { useSearchInputStyles } from './componentStyles/SearchInput.style'

type SearchInputProps = Omit<InputProps, 'onChange' | 'onSubmit' | 'value'> & {
  placeholder?: string
  value?: string
  shareKey?: string
  onClear?: () => void
  onChange?: (searchValue: SearchValue) => void
  onSubmit?: () => void
}

export const SearchInput = forwardRef<SearchInputProps, 'div'>(
  (props: SearchInputProps, ref: ForwardedRef<HTMLInputElement>) => {
    const {
      placeholder,
      value,
      shareKey,
      onChange,
      onClear,
      onSubmit,
      ...rest
    } = props
    const { searchValue, setInputString, getFormattedInput } =
      useSearchInputString({ shareKey })
    const id = useId()
    const handleInputChangedInternal = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      //if externally controlled, don't set the internal input value, just hand back the formatting change
      if (value !== undefined) {
        const { clean, formatted: inputString } = getFormattedInput(
          e.target.value,
        )
        onChange?.(SearchValue.fromValueAndLabel(clean, inputString))
        return
      }

      const searchValue = setInputString(e.target.value)
      onChange?.(searchValue)
    }

    const handleClearedInternal = () => {
      setInputString('')
      onChange?.(SearchValue.empty)
      onClear?.()
    }

    const resolvedInputValue = tryGetValueOrDefault(value, searchValue.label)
    const inputEmpty = resolvedInputValue === ''

    const styles = useSearchInputStyles()

    return (
      <Flex sx={styles.container} {...rest}>
        <InputGroup>
          <Input
            id={id}
            ref={ref}
            sx={styles.input}
            placeholder={placeholder}
            value={resolvedInputValue}
            onChange={handleInputChangedInternal}
            onSubmit={onSubmit}
          />
          <InputRightElement pointerEvents="auto">
            {inputEmpty ? (
              <Icon as={SearchIcon} sx={styles.icon} />
            ) : (
              <MangoCloseButton
                size={Size.sm}
                onClick={handleClearedInternal}
              />
            )}
          </InputRightElement>
        </InputGroup>
      </Flex>
    )
  },
)
